import {
    Bounds, container, namespace, Point,
} from '../../../../../../FrontendBundle/Resources/js/ofcs';
import TreeNode from './treeNode';
import Visualizer from './visualizer';
import Intersections from './intersections';

/**
 */
export default class Tree {
    /**
     * @type {number}
     */
    id = null;

    /**
     * @type {string}
     */
    type = null;

    /**
     * @type {AbstractItem}
     */
    item = null;

    /**
     * @type {number}
     */
    maxDepth = null;

    /**
     * @type {TreeNode}
     */
    root = null;

    /**
     *
     * @type {Line[]}
     * @private
     */
    _lines = [];

    /**
     * @type {Bounds}
     * @private
     */
    _bounds = null;

    /**
     * @param {number} id
     * @param {string} type
     * @param {AbstractItem} item
     * @param {Bounds} bounds
     * @param {Line[]} lines
     * @param {number} maxDepth
     */
    constructor(id, type, item, bounds, lines, maxDepth = 5) {
        this.id = id;
        this.type = type;
        this.item = item;
        this.maxDepth = maxDepth;

        this.update(bounds, lines);
    }

    /**
     * @return {Line[]}
     */
    get lines() {
        return this._lines;
    }

    /**
     * @return {Bounds}
     */
    get bounds() {
        return this._bounds;
    }

    /**
     * Update tree, replacing the root node
     *
     * @param {Bounds} bounds
     * @param {Line[]} lines
     * @return {Tree}
     */
    update(bounds, lines) {
        this._bounds = bounds;
        this._lines = lines;
        this.root = this.createNode(lines, bounds, this.maxDepth, 0, null, 'root');

        return this;
    }

    /**
     * Return root node
     *
     * @return {TreeNode}
     */
    getRoot() {
        return this.root;
    }

    /**
     * Remove all visualizations
     */
    removeVisual() {
        this.getVisualizer().clear();
    }

    /**
     * @param {Ofcs.Editor.Svg} svgParam
     * @param {SVGElement} layerParam
     * @return {Ofcs.Editor.Svg.DrawHelper}
     */
    createDrawHelper(svgParam, layerParam) {
        let svg = svgParam;
        let layer = layerParam;

        if (!svg || !layer) {
            const canvas = container.get('canvas');
            svg = canvas.getSvgWrapper();
            layer = canvas.getLayerManager().getLayerByItem(this.item).getChildLayer('collisions').getElement();
        }

        return new Ofcs.Editor.Svg.DrawHelper(svg, layer);
    }

    /**
     * @param {Ofcs.Editor.Svg} svg
     * @param {SVGElement} layer
     * @return {Visualizer}
     */
    getVisualizer(svg, layer) {
        if (!this.visualizer) {
            this.visualizer = new Visualizer(this.createDrawHelper(svg, layer));
        }
        return this.visualizer;
    }

    /**
     * @param {Ofcs.Editor.Svg} svg
     * @param {SVGElement} layer
     */
    showVisual(svg, layer) {
        const visualizer = this.getVisualizer(svg, layer);
        visualizer.clear();
        visualizer.visualizeTree(this);
    }

    /**
     * Create a node
     *
     * @param {Line[]} lines
     * @param {Bounds} bounds
     * @param {number} maxDepth
     * @param {number} depthParam
     * @param {TreeNode} parent
     * @param {string} type
     * @return {TreeNode}
     */
    createNode(lines, bounds, maxDepth, depthParam, parent, type) {
        const depth = depthParam || 0;

        const inLines = [];
        let inPoints = 0;
        let areaBounds;

        lines.forEach((line) => {
            // const inFrom = bounds.contains(line.startPoint);
            // const inTo = bounds.contains(line.endPoint);
            const inFrom = line.startPoint.x + 1 >= bounds.topLeft.x && line.startPoint.x - 1 <= bounds.bottomRight.x
                && line.startPoint.y + 1 >= bounds.topLeft.y && line.startPoint.y - 1 <= bounds.bottomRight.y;
            const inTo = line.endPoint.x + 1 >= bounds.topLeft.x && line.endPoint.x - 1 <= bounds.bottomRight.x
                && line.endPoint.y + 1 >= bounds.topLeft.y && line.endPoint.y - 1 <= bounds.bottomRight.y;

            if (inFrom || inTo) {
                inLines.push(line);
                inPoints += inFrom + inTo;

                if (!areaBounds) {
                    areaBounds = new Bounds(line.startPoint);
                }

                areaBounds.extend(line.startPoint);
                areaBounds.extend(line.endPoint);
            }
        });

        if (!areaBounds) {
            return null;
        }

        const leftBounds = new Bounds(bounds.topLeft.clone());
        const rightBounds = new Bounds(bounds.bottomRight.clone());

        const width = bounds.getWidth();
        const height = bounds.getHeight();
        if (width > height) {
            const dummyX = bounds.topLeft.x + (width / 2);
            leftBounds.extend(new Point(dummyX, bounds.bottomRight.y));
            rightBounds.extend(new Point(dummyX, bounds.topLeft.y));
        } else {
            const dummyY = bounds.topLeft.y + (height / 2);
            leftBounds.extend(new Point(bounds.bottomRight.x, dummyY));
            rightBounds.extend(new Point(bounds.topLeft.x, dummyY));
        }

        if (depth > 1 && parent && parent.getAreaBounds().equal(areaBounds)) {
            return null;
        }

        const node = new TreeNode(
            areaBounds,
            bounds,
            inLines,
            depth,
        );

        if (depth < maxDepth && inPoints > 1) {
            const left = this.createNode(lines, leftBounds, maxDepth, depth + 1, node, `${type} -> left`);
            const right = this.createNode(lines, rightBounds, maxDepth, depth + 1, node, `${type} -> right`);
            if (left && right) {
                node.setLeft(left);
                node.setRight(right);
            }
        }

        return node;
    }

    /**
     * Intersect this tree with another tree
     *
     * @param {Tree} tree
     * @return {Intersections}
     */
    intersect(tree) {
        const intersections = new Intersections();

        // TODO: only here to avoid nonexistant tree for combined groups
        if (!tree) {
            return intersections;
        }

        if (this.root.intersectTree(tree, intersections)) {
            this._intersectNodeToTree(this.root, tree, intersections);
        }

        return intersections;
    }

    /**
     * Intersect a node with a tree
     *
     * @param {TreeNode} node
     * @param {Tree} tree
     * @param {Intersections} intersections
     * @private
     */
    _intersectNodeToTree(node, tree, intersections) {
        if (node.isLeaf()) {
            node.intersectTree(tree, intersections);
            return;
        }

        if (node.hasLeft()) {
            const left = node.getLeft();
            if (left.intersectTree(tree, intersections)) {
                this._intersectNodeToTree(left, tree, intersections);
            }
        }

        if (node.hasRight()) {
            const right = node.getRight();
            if (right.intersectTree(tree, intersections)) {
                this._intersectNodeToTree(right, tree, intersections);
            }
        }
    }
}

namespace('Ofcs.Editor.Collision');
window.Ofcs.Editor.Collision.Tree = Tree;
